import {GqlModel} from "../graphql/graphql.model";
import {JSONModel, jsonProperty} from "../json.model";
import {ContactModel, contactModelFactory} from "../contact/contact.model";
import {AddressModel, addressModelFactory} from "../address/address.model";
import gql from "graphql-tag";

export type DeliveryMethod = "STANDARD" | "EXPEDITED";
export type ShippingDestination = "CUSTOMER_SITE" | "INSTALLATION_PARTNER" | "STAGING_WAREHOUSE";

export const deliveryMethodMap = {
  STANDARD: "Standard Delivery",
  EXPEDITED: "Expedited Delivery",
};

export const shippingDestinationMap = {
  CUSTOMER_SITE: "Customer Physical Address",
  INSTALLATION_PARTNER: "Installation Partner",
  STAGING_WAREHOUSE: "Staging Warehouse",
};

export const orderFormFragment: any = gql`
  fragment hardwareOrderAddress on Address {
    name1
    name2
    line1
    line2
    city
    region
    country
    postalCode
  }

  fragment hardwareOrderContact on Contact {
    firstName
    lastName
    emailAddress
    phoneNumber
    phoneNumber2
    locale
  }

  fragment orderFormData on HardwareOrderForm {
    id
    projectId
    siteId
    isMigrationActive
    name

    header {
      billToContact { ...hardwareOrderContact }
      deliveryContact { ...hardwareOrderContact }
      installationContact { ...hardwareOrderContact }
      billToAddress { ...hardwareOrderAddress }
      siteAddress { ...hardwareOrderAddress }
      shipToAddress { ...hardwareOrderAddress }
      configurationAddress { ...hardwareOrderAddress }
      shippingDestination
      deliveryMethod
      deliveryInformation
      dealIds
      currency
      sourceDocumentationLink
      erpAccount
      targetDeliveryDate
      estimatedDeliveryDate
      estimatedHardwareOrderDate
      requestedDeliveryDate
      orderTotal
      salValNumber
      opgNumber
      extraPricingInformation
      resellerInformation
      resellerInstructions
      shipCompleted
      floorLevel
      orderNotificationEmailList
      orderAcceptanceCompleted
    }

    devices {
      id
      entity
      productNumber
      description
      deviceKey
      cataloguePrice
      orderQuantity
      childrenQuantity
      extendedPrice
      logisticsCode
      logisticsPrice
      bundleNumbers
      shaded
      indented
      isStandaloneAccessory
      multipleOrderNumbers
      orderNumber
      orderDate
      splitId
    }
  }
`;

export class OrderFormHeaderModel extends JSONModel {
  @jsonProperty(contactModelFactory) billToContact: ContactModel;
  @jsonProperty(contactModelFactory) deliveryContact: ContactModel;
  @jsonProperty(contactModelFactory) installationContact: ContactModel;
  @jsonProperty(addressModelFactory) billToAddress: AddressModel;
  @jsonProperty(addressModelFactory) siteAddress: AddressModel;
  @jsonProperty(addressModelFactory) shipToAddress: AddressModel;
  @jsonProperty(addressModelFactory) configurationAddress: AddressModel;
  @jsonProperty("string") shippingDestination: ShippingDestination;
  @jsonProperty("string") deliveryMethod: DeliveryMethod;
  @jsonProperty("string") deliveryInformation: string;
  @jsonProperty("string") dealIds: string;
  @jsonProperty("string") currency: string;
  @jsonProperty("string") sourceDocumentationLink: string;
  @jsonProperty("string") erpAccount: string;
  @jsonProperty("stringDate") targetDeliveryDate: string;
  @jsonProperty("stringDate") estimatedDeliveryDate: string;
  @jsonProperty("stringDate") estimatedHardwareOrderDate: string;
  @jsonProperty("stringDate") requestedDeliveryDate: string;
  @jsonProperty("string") orderTotal: string;
  @jsonProperty("string") salValNumber: string;
  @jsonProperty("string") opgNumber: string;
  @jsonProperty("string") extraPricingInformation: string;
  @jsonProperty("string") resellerInformation: string;
  @jsonProperty("string") resellerInstructions: string;
  @jsonProperty("boolean") shipCompleted: boolean;
  @jsonProperty("string") floorLevel: string;
  @jsonProperty("string") orderNotificationEmailList: string;
  @jsonProperty("boolean") orderAcceptanceCompleted: boolean;
}

export class OrderFormLineItemModel extends JSONModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") entity: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") description: string;
  @jsonProperty("string") deviceKey: string;

  @jsonProperty("string") cataloguePrice: string;
  @jsonProperty("string") orderQuantity: string;
  @jsonProperty("string") childrenQuantity: string;
  @jsonProperty("string") extendedPrice: string;

  @jsonProperty("string") logisticsCode: string;
  @jsonProperty("string") logisticsPrice: string;

  @jsonProperty("string") bundleNumbers: string;

  @jsonProperty("boolean") shaded: boolean;
  @jsonProperty("boolean") indented: boolean;
  @jsonProperty("boolean") isStandaloneAccessory: boolean;

  @jsonProperty("boolean") multipleOrderNumbers: boolean;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("stringDate") orderDate: string;
  @jsonProperty("string") splitId: string;

  setOrderNumberAndDate(orderNumber: string, orderDate: string) {
    this.multipleOrderNumbers = false;
    this.orderNumber = orderNumber || "";
    this.orderDate = orderDate || "";
  }
}

export class OrderFormGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") projectId: string;
  @jsonProperty("string") siteId: string;
  @jsonProperty("string") name: string;
  @jsonProperty("boolean") isMigrationActive: boolean;

  @jsonProperty(OrderFormHeaderModel) header: OrderFormHeaderModel;
  @jsonProperty([OrderFormLineItemModel]) devices: OrderFormLineItemModel[];
}
